/**
 * BookingStepFrame: the booking steps' frame, shared by the Mini App's DetailsPage, PaymentPage and
 * ConfirmPage and the studio website's checkout (shared-client-screens D2). Back, the pinned photo
 * header, the rising sheet over it, the bar pinned under the sheet, and whatever rides over the
 * page (an overlay, a sheet). Props only: each page keeps its own rails, reads and navigation, and
 * passes its own CSS module (each step tunes the same class names).
 */
import type { ReactNode, Ref } from 'react';
import { FlowBackButton } from './FlowBackButton';

export interface BookingStepFrameProps {
  /** The step's CSS module: page, back, scroll, heroSpacer, sheet, grab and body. */
  styles: Readonly<Record<string, string>>;
  /** The pinned photo header (BookingHero). */
  hero: ReactNode;
  /** Back; no button when the step has none (a finished booking's views). */
  onBack?: () => void;
  backDisabled?: boolean;
  /** The bar pinned under the sheet. */
  bar?: ReactNode;
  /** Drawn over the page after the bar: an overlay, a sheet. */
  overlay?: ReactNode;
  /** The scroller, for the page's keyboard rail. */
  scrollRef?: Ref<HTMLDivElement>;
  children: ReactNode;
}

export function BookingStepFrame({ styles, hero, onBack, backDisabled, bar, overlay, scrollRef, children }: BookingStepFrameProps) {
  return (
    <div className={styles.page}>
      {onBack && <FlowBackButton className={styles.back} onClick={onBack} disabled={backDisabled} />}

      {/* Hero is PINNED outside the scroller (z-index behind it) so it stays put while the
          sheet scrolls up over it; the transparent spacer in the scroller is the window onto it. */}
      {hero}

      <div className={styles.scroll} ref={scrollRef}>
        <div className={styles.heroSpacer} aria-hidden="true" />
        <div className={styles.sheet}>
          <div className={styles.grab} aria-hidden="true" />
          <div className={styles.body}>{children}</div>
        </div>
      </div>

      {bar}
      {overlay}
    </div>
  );
}
